/**
 * 集中できる時間（勉強・難しい作業・読書に使える時間）
 *
 * 深い集中・ふつうの集中・復習の時間を、カード（今日の合計と上限の目安）とタイムラインの項目で出す。
 */

import { BookOpen, Brain, Dumbbell, Footprints, NotebookPen } from 'lucide-react'

import type { ExerciseSlot, ExerciseSlotKind, FocusAdvice, FocusSlot, FocusSlotKind } from '@/api/types/sleep'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'

import { formatJstTime, formatMinutes } from '../format'

const KIND_STYLE = new Map<
  FocusSlotKind | ExerciseSlotKind,
  { label: string; icon: typeof Brain; dot: string; box: string; text: string }
>([
  [
    'deep',
    {
      label: '深い集中',
      icon: Brain,
      dot: 'bg-violet-500',
      box: 'bg-violet-50/70 dark:bg-violet-950/30',
      text: 'text-violet-700 dark:text-violet-300',
    },
  ],
  [
    'normal',
    {
      label: 'ふつうの集中',
      icon: BookOpen,
      dot: 'bg-sky-500',
      box: 'bg-sky-50/70 dark:bg-sky-950/30',
      text: 'text-sky-700 dark:text-sky-300',
    },
  ],
  [
    'review',
    {
      label: '復習',
      icon: NotebookPen,
      dot: 'bg-amber-400',
      box: 'bg-amber-50/70 dark:bg-amber-950/30',
      text: 'text-amber-700 dark:text-amber-300',
    },
  ],
  [
    'workout',
    {
      label: '運動',
      icon: Dumbbell,
      dot: 'bg-rose-500',
      box: 'bg-rose-50/70 dark:bg-rose-950/30',
      text: 'text-rose-700 dark:text-rose-300',
    },
  ],
  [
    'walk',
    {
      label: '散歩',
      icon: Footprints,
      dot: 'bg-teal-500',
      box: 'bg-teal-50/70 dark:bg-teal-950/30',
      text: 'text-teal-700 dark:text-teal-300',
    },
  ],
])

const timeRange = (slot: FocusSlot | ExerciseSlot) => `${formatJstTime(slot.start)}〜${formatJstTime(slot.end)}`

/** 集中・運動のために取ってある時間（タイムラインの項目） */
export const FocusSlotItem = ({ slot, todoTitles }: { slot: FocusSlot | ExerciseSlot; todoTitles: string[] }) => {
  const style = KIND_STYLE.get(slot.kind)
  const Icon = style?.icon ?? Brain
  return (
    <li className="relative pb-4 pl-4">
      <span
        className={cn('absolute top-1.5 -left-[5px] size-2.5 rounded-full border-2 border-background', style?.dot)}
      />
      <div className={cn('rounded-lg px-2.5 py-1.5', style?.box)}>
        <p className="flex flex-wrap items-center gap-x-1.5 text-sm">
          <Icon className={cn('size-3.5 shrink-0', style?.text)} />
          <span className="font-semibold tabular-nums">{timeRange(slot)}</span>
          {slot.title}
          <span className="text-xs text-muted-foreground">{formatMinutes(slot.minutes)}</span>
        </p>
        <p className="text-xs text-muted-foreground">{slot.detail}</p>
        {todoTitles.length > 0 && (
          <p className={cn('mt-0.5 text-xs wrap-break-word', style?.text)}>やること: {todoTitles.join(' / ')}</p>
        )}
      </div>
    </li>
  )
}

export const FocusAdviceCard = ({ advice, now }: { advice: FocusAdvice; now: number }) => {
  const upcoming = advice.slots.filter((s) => parseUtcDate(s.end).getTime() >= now)
  const ratio = advice.deep_cap_min > 0 ? Math.min(advice.deep_total_min / advice.deep_cap_min, 1) : 0
  return (
    <section className="space-y-2">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold">
        <Brain className="size-4" />
        集中できる時間
      </h3>
      <p className="text-sm">{advice.summary}</p>
      <div className="space-y-1">
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-violet-500" style={{ width: `${ratio * 100}%` }} />
        </div>
        <p className="text-[11px] text-muted-foreground">
          深い集中 {formatMinutes(advice.deep_total_min)} / 目安 {formatMinutes(advice.deep_cap_min)}
        </p>
      </div>
      {upcoming.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {upcoming.map((slot) => {
            const style = KIND_STYLE.get(slot.kind)
            return (
              <li
                key={`${slot.kind}-${slot.start}`}
                className={cn('rounded-full px-2 py-0.5 text-xs tabular-nums', style?.box, style?.text)}
              >
                {timeRange(slot)} {style?.label}
              </li>
            )
          })}
        </ul>
      )}
      {advice.notes.map((note) => (
        <p key={note} className="text-xs text-amber-700 dark:text-amber-300">
          {note}
        </p>
      ))}
      {advice.tips.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">集中と復習のコツ</summary>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {advice.tips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}
